/**
 * layer-list-base.js  (refactored)
 * Routing only — every panel is built in layer-list-panels.js.
 * Pure DOM helpers live in layer-list-ui-helpers.js.
 */

import { showCPOMillClusters } from "@/views/mills/widgets/layer-list/cpo-mills-cluster";
import { showP1InField } from "@/views/mills/widgets/layer-list/P1-in-field";
import { releafTaggedMills } from "@/views/mills/widgets/layer-list/releaf-tagged-mills";
import { createGroupLayerDropdown } from "./layer-list-ui-helpers";
import {
  buildSimpleSummaryPanel,
  buildReleafAssetsPanel,
  buildCustomerFactoriesPanel,
  buildProcessingFactoriesPanel,
  buildBiocharPartnersPanel,
  buildMaizeFarmsPanel,
  buildOilPalmSliderPanel,
  buildLGASummarySliderPanel,
  buildRoofFootprintSliderPanel,
  buildMaizeProductionPanel,
  buildRawMaterialSourcePanel,
  buildBiocharApplicationsPanel,
  buildSoilAnalysisPanel,
  buildSoilRasterPanel,
  buildMaizeYieldRasterPanel,
  buildMaizeSuitabilityRasterPanel,
  buildMaizeAreaPanel,
} from "./layer-list-panels";

// ─── Layers that should always start hidden ───────────────────────────────────
const HIDDEN_BY_DEFAULT = new Set([
  "Community transaction summary",
  "Oil Palm Customers",
  "Mills Distance",
  "Logistics Depot",
  "P1 LMV Pickup",
  "Akwa Ibom Plantations",
  "P1 Mill Aggregation",
  "LGA transaction summary",
  "Minigrids",
  "Deforestation Protected Areas",
  "P1 Aggregation Points",
  "Community Price Projections",
  "Active P1 Purchasing Communities",
  "Current P1 price in communities",
  "Nigeria Ward Layer",
  "Nigeria States Boundaries",
  "Nigeria LGA Layer",
  "Key Conservation Areas (HCV)",
  "High Carbon Stock Areas",
  "Industrial Parks",
  "Forest Cover Loss",
  "Forest Cover Gain",
  "Infrastructure",
  "Mill Sustainability Scores",
]);

// ─── Layers that get hidden + a legend-only panel ────────────────────────────
const LEGEND_ONLY_HIDDEN = new Set([
  "Maize Soil Analysis",
  "Maize Suitability",
  "Processed Maize Yield",
  "Maize Yield Projection",
]);

// ─── Main export ──────────────────────────────────────────────────────────────

export function layerListActions(
  event,
  weeklyP1YieldSlider,
  Slider,
  oilPalmClusterSlider,
  p1LGASummarySlider,
  buidingFootprintSlider,
  daysSinceLastDiscoverySlider,
  tonnesBoughtTaggedMillsSlider,
  numberOfTxnSlider,
  aveP1PerMillSlider,
  sliderTonnesAtField,
  sliderDaysSincePurchase,
  pickupVehicleCheckboxes,
  agentsDropdownList,
  customerFactoryTypeFilter,
  customerFactoryTypeFilterReset,
  releafFactoryTypeFilter,
  releafFactoryTypeFilterReset,
  p1AtFieldReset,
  cpoTaggedMillsFilterReset,
  gtForDaySinceLastPurchase,
  ltForDaySinceLastPurchase,
  gtForTonnesPurchased,
  ltForTonnesPurchased,
  customerFactoryStatusFilter,
  processedFactoryStatusFilter,
  processedFarmStatusFilter,
  releafFactoryStatusFilter,
  gtDaysSinceLastDiscovery,
  ltDaysSinceLastDiscovery,
  gtTonnesBoughtTaggedMills,
  ltTonnesBoughtTaggedMills,
  gtNumberOfTxn,
  ltNumberOfTxn,
  gtAveP1PerMill,
  ltAveP1PerMill,
  gtBuidingFootprint,
  ltBuidingFootprint,
  gtWeeklyP1Yield,
  ltWeeklyP1Yield,
  gtOilPalmCluster,
  ltOilPalmCluster,
  gtP1LGASummary,
  ltP1LGASummary,
  oilPalmClusterReset,
  handleShowP1Summary,
  handleReleafFactorySummary,
  handleCustomerFactorySummary,
  handleCPOTaggedReleafSummary,
  handleOilpalmClusterSummary,
  handlesNgnLGASummary,
  handlesWardsSummary,
  handleClusteredMillSummary,
  handleDeforestedOilpalmClusterSummary,
  handleRMSummary,
  maize2022ProductionLayer,
  maize2023ProductionLayer,
  RasterStretchRenderer,
  toast,
  handleShowBiocharLayerSummary,
  biocharPartnersFilter,
  biocharTypeFilter,
  self
) {
  const eventItem = event.item;

  // Shared state for Feedstock/Offtake dropdowns — must live here so it
  // persists across re-renders of the same layerList session.
  const layerSelectionState = {
    FeedStock: new Set(["all"]),
    OffTake: new Set(["all"]),
  };

  // ── 1. Simple hidden layers ─────────────────────────────────────────────────
  if (HIDDEN_BY_DEFAULT.has(eventItem.title)) {
    eventItem.visible = false;
  }

  // ── 2. Hidden + legend-only panel ──────────────────────────────────────────
  if (LEGEND_ONLY_HIDDEN.has(eventItem.title)) {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = {
        content: ["legend"],
        className: "esri-icon-sliders-horizontal",
        title: "Layer Properties",
      };
    }
  }

  // ── 3. Default legend panel for all non-group layers ───────────────────────
  //    (applied broadly; specific panels below will override it)
  if (eventItem.layer.type !== "group") {
    eventItem.panel = {
      content: "legend",
      open: false,
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }

  // ── 4. Feedstock / Offtake group dropdowns ─────────────────────────────────
  if (eventItem.title === "Feedstock Layers") {
    eventItem.panel = {
      content: createGroupLayerDropdown(
        eventItem.layer,
        ["Maize Production 2023", "Niger Delta Oil Palm", "Processing Factories"],
        layerSelectionState
      ),
      open: true,
      className: "esri-layer-list__item-panel--inline",
    };
  }

  if (eventItem.title === "Offtake Layers") {
    eventItem.panel = {
      content: createGroupLayerDropdown(
        eventItem.layer,
        ["Soil pH (Nigeria)", "Soil Carbon", "Sand Texture", "Commercial Farms", "Geo-Fenced Farms", "Maize Farms"],
        layerSelectionState
      ),
      open: true,
      className: "esri-layer-list__item-panel--inline",
    };
  }

  // ── 5. Simple summary-button panels ────────────────────────────────────────
  if (eventItem.title === "Potential Deforestation Encroachment ") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildSimpleSummaryPanel(handleDeforestedOilpalmClusterSummary);
    }
  }

  if (eventItem.title === "Nigeria LGA Boundaries") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildSimpleSummaryPanel(handlesNgnLGASummary);
    }
  }

  if (eventItem.title === "Nigeria Ward Boundaries") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildSimpleSummaryPanel(handlesWardsSummary);
    }
  }

  // ── 6. Releaf Assets ────────────────────────────────────────────────────────
  if (eventItem.title === "Releaf Assets") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildReleafAssetsPanel({
        releafFactoryTypeFilter,
        releafFactoryTypeFilterReset,
        releafFactoryStatusFilter,
        handleReleafFactorySummary,
      });
    }
  }

  // ── 7. Customer Factories ───────────────────────────────────────────────────
  if (eventItem.title === "Customer Factories") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildCustomerFactoriesPanel({
        customerFactoryTypeFilter,
        customerFactoryTypeFilterReset,
        customerFactoryStatusFilter,
        handleCustomerFactorySummary,
      });
    }
  }

  // ── 8. P1 Purchases (Field) — delegates to existing helper ─────────────────
  if (eventItem.title === "P1 Purchases (Field)") {
    const __ret = showP1InField(
      eventItem,
      sliderTonnesAtField,
      Slider,
      sliderDaysSincePurchase,
      gtForDaySinceLastPurchase,
      ltForDaySinceLastPurchase,
      gtForTonnesPurchased,
      ltForTonnesPurchased,
      pickupVehicleCheckboxes,
      agentsDropdownList,
      p1AtFieldReset,
      sliderResetP1AtField,
      handleShowP1Summary
    );
    sliderTonnesAtField = __ret.sliderTonnesAtField;
    sliderDaysSincePurchase = __ret.sliderDaysSincePurchase;
    eventItem.visible = false;
    if (eventItem.panel) eventItem.panel.open = false;
  }

  function sliderResetP1AtField() {
    if (sliderDaysSincePurchase) sliderDaysSincePurchase.values = [0];
    if (sliderTonnesAtField) sliderTonnesAtField.values = [0];
  }

  // ── 9. Niger Delta Planted Oil Palm ─────────────────────────────────────────
  if (eventItem.title === "Niger Delta Planted Oil Palm") {
    eventItem.visible = true;
    if (eventItem.layer.type !== "group") {
      const result = buildOilPalmSliderPanel(Slider, {
        gtWeeklyP1Yield,
        ltWeeklyP1Yield,
        gtOilPalmCluster,
        ltOilPalmCluster,
        oilPalmClusterReset,
        handleOilpalmClusterSummary,
      });
      weeklyP1YieldSlider = result.weeklyP1YieldSlider;
      oilPalmClusterSlider = result.oilPalmClusterSlider;
      eventItem.panel = result.panel;
    }
  }

  // ── 10. P1 LGA Summary ──────────────────────────────────────────────────────
  if (eventItem.title === "P1 LGA Summary") {
    eventItem.visible = false;
    const result = buildLGASummarySliderPanel(Slider, { gtP1LGASummary, ltP1LGASummary });
    p1LGASummarySlider = result.p1LGASummarySlider;
    eventItem.panel = result.panel;
  }

  // ── 11. Maize Production 2022 / 2023 ───────────────────────────────────────
  if (
    eventItem.title === "Maize Production 2022" ||
    eventItem.title === "Maize Production 2023"
  ) {
    eventItem.actionsSections = [[{
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
      id: "toggle-panel",
    }]];

    const layer = eventItem.title.includes("2022")
      ? maize2022ProductionLayer
      : maize2023ProductionLayer;
    const year = eventItem.title.includes("2022") ? 2022 : 2023;

    eventItem.panel = {
      content: buildMaizeProductionPanel(layer, year),
      open: false,
      visible: true,
    };
  }

  // ── 12. Raw Material Source ─────────────────────────────────────────────────
  if (eventItem.title === "Raw Material Source") {
    eventItem.panel = buildRawMaterialSourcePanel(eventItem.layer, { handleRMSummary });
  }

  // ── 13. Biochar Applications ────────────────────────────────────────────────
  if (eventItem.title === "Biochar Applications") {
    eventItem.visible = true;
    eventItem.panel = buildBiocharApplicationsPanel(self, {
      biocharTypeFilter,
      handleShowBiocharLayerSummary,
      toast,
    });
  }

  // ── 14. Roof Footprint ──────────────────────────────────────────────────────
  if (eventItem.title === "Roof Footprint") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      const result = buildRoofFootprintSliderPanel(Slider, {
        gtBuidingFootprint,
        ltBuidingFootprint,
      });
      buidingFootprintSlider = result.buidingFootprintSlider;
      eventItem.panel = result.panel;
    }
  }

  // ── 15. Confirmed Mills — delegates to existing helper ──────────────────────
  if (eventItem.title === "Confirmed Mills") {
    const __ret = releafTaggedMills(
      eventItem,
      daysSinceLastDiscoverySlider,
      Slider,
      tonnesBoughtTaggedMillsSlider,
      numberOfTxnSlider,
      gtDaysSinceLastDiscovery,
      ltDaysSinceLastDiscovery,
      gtTonnesBoughtTaggedMills,
      ltTonnesBoughtTaggedMills,
      gtNumberOfTxn,
      ltNumberOfTxn,
      cpoTaggedMillsFilterReset,
      sliderResetCPOtaggedMills,
      handleCPOTaggedReleafSummary
    );
    daysSinceLastDiscoverySlider = __ret.daysSinceLastDiscoverySlider;
    tonnesBoughtTaggedMillsSlider = __ret.tonnesBoughtTaggedMillsSlider;
    numberOfTxnSlider = __ret.numberOfTxnSlider;
  }

  function sliderResetCPOtaggedMills() {
    if (daysSinceLastDiscoverySlider) daysSinceLastDiscoverySlider.values = [0];
    if (tonnesBoughtTaggedMillsSlider) tonnesBoughtTaggedMillsSlider.values = [0];
    if (numberOfTxnSlider) numberOfTxnSlider.values = [0];
  }

  // ── 16. Processing Factories ────────────────────────────────────────────────
  if (eventItem.title === "Processing Factories") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildProcessingFactoriesPanel({ processedFactoryStatusFilter });
    }
  }

  // ── 17. Biochar Partners ────────────────────────────────────────────────────
  if (eventItem.title === "Biochar Partners") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildBiocharPartnersPanel({ biocharPartnersFilter });
    }
  }

  // ── 18. Maize Farms ─────────────────────────────────────────────────────────
  if (eventItem.title === "Maize Farms") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildMaizeFarmsPanel({ processedFarmStatusFilter });
    }
  }

  // ── 19. Soil Analysis (feature layer) ──────────────────────────────────────
  if (eventItem.title === "Soil Analysis") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildSoilAnalysisPanel(eventItem);
    }
  }

  // ── 20. Soil Analysis (Raster View) ────────────────────────────────────────
  if (eventItem.title === "Soil Analysis (Raster View)") {
    eventItem.visible = false;
    buildSoilRasterPanel(eventItem, RasterStretchRenderer);
  }

  // ── 21. Maize Yield (Projected) ─────────────────────────────────────────────
  if (eventItem.title === "Maize Yield (Projected)") {
    eventItem.visible = false;
    buildMaizeYieldRasterPanel(eventItem);
  }

  // ── 22. Maize Suitability (Projected) ──────────────────────────────────────
  if (eventItem.title === "Maize Suitability (Projected)") {
    eventItem.visible = false;
    buildMaizeSuitabilityRasterPanel(eventItem);
  }

  // ── 23. Maize Growing Area ──────────────────────────────────────────────────
  if (eventItem.title === "Mazie Growing Area (Main Season)") {
    eventItem.visible = false;
    if (eventItem.layer.type !== "group") {
      eventItem.panel = buildMaizeAreaPanel();
    }
  }

  // ── 24. PIND Mills — delegates to existing helper ──────────────────────────
  if (eventItem.title === "PIND Mills") {
    aveP1PerMillSlider = showCPOMillClusters(
      eventItem,
      aveP1PerMillSlider,
      Slider,
      gtAveP1PerMill,
      ltAveP1PerMill,
      handleClusteredMillSummary
    );
  }

  // ── Return mutated slider references back to MyMap.vue ────────────────────
  return {
    weeklyP1YieldSlider,
    oilPalmClusterSlider,
    p1LGASummarySlider,
    buidingFootprintSlider,
    daysSinceLastDiscoverySlider,
    tonnesBoughtTaggedMillsSlider,
    numberOfTxnSlider,
    aveP1PerMillSlider,
    sliderTonnesAtField,
    sliderDaysSincePurchase,
    pickupVehicleCheckboxes,
  };
}